import { React, useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faLocationCrosshairs,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/secondpage.css";
import "../styles/secondpage-responsive.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ListYourselfModal from "../components/ListYourselfModal";

function Secondpage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [practitioners, setPractitioners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [apiCallCompleted, setApiCallCompleted] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [popularSpecialties, setPopularSpecialties] = useState([]);
  const suggestionsRef = useRef(null);
  const form = useRef(null);
  const navigate = useNavigate();


  const handleInputClick = () => {
    setSearchTriggered(true);
    // Only fetch if we have a search query
    if (searchQuery) {
      fetchPractitionersImmediate(searchQuery);
    }
  };

  // Immediate search function for name field
  const fetchPractitionersByName = async (query) => {
    if (!query) return;

    setIsLoading(true);
    setApiCallCompleted(false);
    setPractitioners([]);
    try {
      const url = `https://directory.virtualtriage.ai/doctors/search/?query=${query}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch practitioners");
      const data = await response.json();
      setPractitioners(data.data || []);
    } catch (error) {
      console.error("Error fetching practitioners:", error);
      setPractitioners([]);
    } finally {
      setIsLoading(false);
      setApiCallCompleted(true);
    }
  };

  // Search by location
  const fetchPractitionersByLocation = async (location) => {
    if (!location) return;

    setIsLoading(true);
    setApiCallCompleted(false);
    setPractitioners([]);
    try {
      const url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${location}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch practitioners");
      const data = await response.json();
      setPractitioners(data.data || []);
    } catch (error) {
      console.error("Error fetching practitioners:", error);
      setPractitioners([]);
    } finally {
      setIsLoading(false);
      setApiCallCompleted(true);
    }
  };

  // Search by specialty
  const fetchPractitionersBySpecialty = async (specialty) => {
    if (!specialty) return;

    setIsLoading(true);
    setApiCallCompleted(false);
    setPractitioners([]);
    try {
      const url = `https://directory.virtualtriage.ai/doctors/search/?specialty=${specialty}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch practitioners");
      const data = await response.json();
      setPractitioners(data.data || []);
    } catch (error) {
      console.error("Error fetching practitioners:", error);
      setPractitioners([]);
    } finally {
      setIsLoading(false);
      setApiCallCompleted(true);
    }
  };

  // Immediate search function for name field focus
  const fetchPractitionersImmediate = async (query) => {
    if (!query) return; // Don't make API call if query is empty

    setIsLoading(true);
    setApiCallCompleted(false);
    setPractitioners([]);
    try {
      let url = "";
      const effectiveQuery = query !== undefined ? query : searchQuery;
      if (effectiveQuery && selectedLocation && specialtyInput) {
        url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}&specialty=${specialtyInput}&query=${effectiveQuery}`;
      } else if (effectiveQuery && selectedLocation) {
        url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}&query=${effectiveQuery}`;
      } else if (effectiveQuery && specialtyInput) {
        url = `https://directory.virtualtriage.ai/doctors/search/?query=${effectiveQuery}&specialty=${specialtyInput}`;
      } else if (selectedLocation && specialtyInput) {
        url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}&specialty=${specialtyInput}`;
      } else if (effectiveQuery) {
        url = `https://directory.virtualtriage.ai/doctors/search/?query=${effectiveQuery}`;
      } else if (selectedLocation) {
        url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}`;
      } else if (specialtyInput) {
        url = `https://directory.virtualtriage.ai/doctors/search/?specialty=${specialtyInput}`;
      } else {
        setIsLoading(false);
        setApiCallCompleted(true);
        return;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch practitioners");
      const data = await response.json();
      setPractitioners(data.data || []);
    } catch (error) {
      console.error("Error fetching practitioners:", error);
      setPractitioners([]);
    } finally {
      setIsLoading(false);
      setApiCallCompleted(true);
    }
  };

  // Fetch popular specialties
  useEffect(() => {
    const fetchPopularSpecialties = async () => {
      try {
        const response = await fetch("https://directory.virtualtriage.ai/doctors/popular/specialties/");
        if (!response.ok) throw new Error("Failed to fetch specialties");
        const data = await response.json();
        setPopularSpecialties(data.data || []);
      } catch (error) {
        console.error("Error fetching specialties:", error);
      }
    };

    fetchPopularSpecialties();
  }, []);

  // Fetch practitioners from API
  useEffect(() => {
    const handler = setTimeout(() => {
      const fetchPractitioners = async () => {
        setIsLoading(true);
        setApiCallCompleted(false);
        setPractitioners([]);

        try {
          let url = "";
          // Build query string based on all fields
          if (searchQuery && selectedLocation && specialtyInput) {
            url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}&specialty=${specialtyInput}&query=${searchQuery}`;
          } else if (searchQuery && selectedLocation) {
            url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}&query=${searchQuery}`;
          } else if (searchQuery && specialtyInput) {
            url = `https://directory.virtualtriage.ai/doctors/search/?query=${searchQuery}&specialty=${specialtyInput}`;
          } else if (selectedLocation && specialtyInput) {
            url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}&specialty=${specialtyInput}`;
          } else if (searchQuery) {
            url = `https://directory.virtualtriage.ai/doctors/search/?query=${searchQuery}`;
          } else if (selectedLocation) {
            url = `https://directory.virtualtriage.ai/doctors/search/location/?location=${selectedLocation}`;
          } else if (specialtyInput) {
            url = `https://directory.virtualtriage.ai/doctors/search/?specialty=${specialtyInput}`;
          } else {
            return; // Don't call API if all empty
          }

          const response = await fetch(url);
          if (!response.ok) throw new Error("Failed to fetch practitioners");

          const data = await response.json();
          setPractitioners(data.data || []);
        } catch (error) {
          console.error("Error fetching practitioners:", error);
          setPractitioners([]);
        } finally {
          setIsLoading(false);
          setApiCallCompleted(true);
        }
      };

      fetchPractitioners();
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery, selectedLocation, specialtyInput]);

  const sendEmail = async (e) => {
    e.preventDefault();

    const formData = {
      name: form.current["name"].value,
      number: "", // Add a field for number if needed
      email: form.current["email"].value,
      message: "Newsletter subscription",
      type: "contact",
    };

    try {
      const response = await fetch(
        "${process.env.REACT_APP_PROD_API_URL}contact_email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send newsletter subscription");
      }

      alert("Request has been sent!");
    } catch (error) {
      console.error("Error sending subscription:", error);
      alert(
        "There was an error sending your subscription. Please try again later."
      );
    }
  };

  // Add fetchLocation
  const fetchLocation = () => {
    setAlertMessage("");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);
          try {
            const response = await fetch(
              `${process.env.REACT_APP_PROD_API_URL}api/v1/doctor/nearby-doctors/?latitude=${latitude}&longitude=${longitude}&km=10000&specialty=${searchQuery}`
            );
            if (!response.ok) {
              throw new Error("Failed to fetch nearby practitioners");
            }
            const data = await response.json();
            setPractitioners(data.data || []);
            navigate("/practitioner-list", {
              state: { practitioners: data.data || [] },
            });
          } catch (error) {
            console.error("Error fetching nearby practitioners:", error);
            alert(
              "Unable to fetch nearby practitioners. Please try again later."
            );
          }
        },
        (error) => {
          console.error("Error fetching location: ", error);
          alert("Unable to fetch location. Please try again.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  // Add fetchAllPractitioners
  const fetchAllPractitioners = async () => {
    setAlertMessage("");
    setIsLoading(true);
    try {
      const response = await fetch(
        "https://directory.virtualtriage.ai/doctors/search/?query= "
      );
      if (!response.ok) {
        throw new Error("Failed to fetch practitioners");
      }
      const data = await response.json();
      
      // Format the practitioners data
      const formattedPractitioners = data.data.map(practitioner => ({
        _id: practitioner._id,
        full_name: practitioner.full_name,
        experience_years: practitioner.experience_years || "Not specified",
        subspecialties: practitioner.subspecialties || [],
        verified: practitioner.verified || false,
        rating: practitioner.rating || 0,
        locations: practitioner.locations || [],
        areas_of_expertise: practitioner.areas_of_expertise || [],
        diseases_and_treatments: practitioner.diseases_and_treatments || [],
        conditions_and_treatments: practitioner.conditions_and_treatments || []
      }));

      setPractitioners(formattedPractitioners);
      navigate("/practitioner-list", {
        state: { 
          practitioners: formattedPractitioners,
          searchType: "all" // Add this to indicate it's a full list view
        },
      });
    } catch (error) {
      console.error("Error fetching practitioners:", error);
      alert("Unable to fetch practitioners. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter practitioners based on the name search term
  const filteredPractitioners = practitioners.filter((practitioner) => {
    if (searchQuery) {
      return (
        practitioner.full_name &&
        practitioner.full_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return true; // Include all if no searchQuery
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setSearchTriggered(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  return (
    <div className="secondpage-container">
      <Navbar />
      {/* Main Banner */}
      <div className="find-body">
        <section className="find-banner">
          <div className="find-hero-text">
            <h1 className="find-practitioners-banner-h1">
              Find Trusted{" "}
              <strong style={{ color: "#00a3bb" }}>
                Healthcare Practitioners
              </strong>{" "}
              Near You &{" "}
              <strong style={{ color: "#00a3bb" }}>Book Appointments</strong>{" "}
              with Ease!
            </h1>
            <p>
              Discover and connect with experienced healthcare practitioners
              near you. Use our advanced search filters to find the right
              specialist by name, location, or specialty, and book appointments
              effortlessly for personalized care.
            </p>
            <h3 style={{ color: "#000", margin: "0" }}>
              Search Practitioners by Name, Location or Specialty:
            </h3>
            <div className="search-container">
              <div className="search-item" ref={suggestionsRef}>
                <div className="search-input-container">
                  {/* Input for name/specialty */}
                  <input
                    type="text"
                    placeholder="Search by Name"
                    value={searchQuery}
                    onClick={() => {
                      setSearchTriggered(true);
                      fetchPractitionersByName(searchQuery || " ");
                    }}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setApiCallCompleted(false);
                      setSearchTriggered(true);
                    }}
                    className="search-input"
                  />

                  {/* Input for city/location */}
                  <input
                    type="text"
                    placeholder="Search by Location"
                    value={selectedLocation}
                    onClick={() => {
                      setSearchTriggered(true);
                      fetchPractitionersByLocation(selectedLocation || " ");
                    }}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="location-input"
                  />
                  {/* {selectedLocation && (
                    <div className="search-suggestions" style={{ zIndex: 2000, position: "absolute", width: "100%" }}>
                      {locationSuggestions
                        .filter((loc) =>
                          loc.toLowerCase().includes(selectedLocation.toLowerCase())
                        )
                        .slice(0, 8)
                        .map((loc) => (
                          <div
                            key={loc}
                            className="suggestion-item"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedLocation(loc);
                              setSearchTriggered(false);
                            }}
                          >
                            {loc}
                          </div>
                        ))}
                    </div>
                  )} */}

                  {/* Specialty input with suggestions */}
                    <input
                      type="text"
                      placeholder="Search by Specialty"
                      value={specialtyInput}
                      onClick={() => {
                        setSearchTriggered(true);
                        fetchPractitionersBySpecialty(specialtyInput || " ");
                      }}
                      onChange={(e) => {
                        setSpecialtyInput(e.target.value);
                        setSearchTriggered(false);
                      }}
                      className="search-input"
                      autoComplete="off"
                      style={{ position: "relative" }}
                    />
                    {specialtyInput && Array.isArray(popularSpecialties) && (
                      <div className="search-suggestions" style={{ zIndex: 2000, position: "absolute", width: "100%" }}>
                        {popularSpecialties
                          .filter((spec) =>
                            spec.name.toLowerCase().includes(specialtyInput.toLowerCase())
                          )
                          .slice(0, 8)
                          .map((spec) => (
                            <div
                              key={spec.id}
                              className="suggestion-item"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setSpecialtyInput(spec.name);
                              }}
                            >
                              {spec.name}
                            </div>
                          ))}
                      </div>
                    )}

                  {/* Practitioner suggestions for name field */}
                  {searchTriggered && (
                    <div className="search-suggestions">
                      {isLoading && <p>Loading...</p>}
                      {!isLoading && practitioners.length === 0 && (
                        <p style={{ color: "gray", marginLeft: "10px" }}>
                          No practitioners found.
                        </p>
                      )}
                      {!isLoading && practitioners.length > 0 && (
                        <ul className="suggestions-list">
                          {filteredPractitioners.map((practitioner) => (
                            <li
                              key={practitioner._id}
                              className="suggestion-item"
                              onClick={() => {
                                navigate(`/profile/${practitioner._id}`);
                              }}
                            >
                              <div className="suggestion-info">
                                <strong>{practitioner.full_name}</strong>
                                <br />
                                <small>
                                  {practitioner?.locations?.[0]?.city || "Unknown City"}, {practitioner?.locations?.[0]?.country || "Unknown Country"}
                                </small>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                <button
                  className="fetch-location-button"
                  onClick={fetchAllPractitioners}
                >
                  <h4 style={{ margin: "5px" }}>View All Practitioners</h4>
                </button>
              </div>
            </div>
            {alertMessage && <p style={{ color: "red" }}>{alertMessage}</p>}
          </div>
          <div className="find-hero-image">
            <img src="../assets/find-doctor.png" alt="Find Practitioners" />
          </div>
        </section>
      </div>

      {/* Why List? */}
      <section className="virtual-care-benefits">
        <div className="benefits-image-2">
          <img src="./assets/Global Exposure.png" alt="Global Exposure" />
        </div>

        <div className="benefits-content">
          <h2>Why Listing Yourself Matters?</h2>
          <p>
            In today's digital-first healthcare world, visibility = credibility.
            Listing yourself opens the door to career growth and global reach
            for less than a sandwich a month, just <strong>£8.99/month</strong>.
          </p>
          <ul className="benefits-list">
            <li>
              <span>✔</span> Gain exposure to patients globally, not just
              locally.
            </li>
            <li>
              <span>✔</span> Attract more consultations with higher
              discoverability.
            </li>
            <li>
              <span>✔</span> Build a loyal, expanding patient base over time.
            </li>
            <li>
              <span>✔</span> Strengthen your personal brand and stand out
              professionally.
            </li>
          </ul>
          <div className="coffee-price-note">
            All this for just <strong>£8.99/month</strong>, a small price for something that actually adds value.
          </div>
          <div className="list-yourself-btn-section">
            <button
              className="cta-button"
              style={{ margin: "10px" }}
              onClick={() =>
              (window.location.href =
                "https://practitioner.virtualtriage.ai/practitioner-signup")
              }
            >
              List Yourself Now!
            </button>

            {/* <ListYourselfModal
              isOpen={isListModalOpen}
              onClose={() => setIsListModalOpen(false)}
            /> */}
          </div>
        </div>
      </section>

      {/* Virtual Care Benefits Section */}
      <section className="virtual-care-benefits">
        <div className="benefits-content">
          <h2>Why Virtual Healthcare Matters?</h2>
          <p>
            Accessing quality healthcare should be convenient, cost-effective,
            and available anywhere. Virtual healthcare bridges the gap by
            eliminating long wait times, reducing travel hassles, and ensuring
            that patients receive expert consultation anytime, anywhere.
          </p>
          <ul className="benefits-list">
            <li>
              <span>✔</span> Skip long queues & waiting rooms
            </li>
            <li>
              <span>✔</span> Get instant access to top specialists
            </li>
            <li>
              <span>✔</span> Affordable care with no hidden costs
            </li>
            <li>
              <span>✔</span> Secure video consultations & e-prescriptions
            </li>
          </ul>
        </div>
        <div className="benefits-image">
          <img
            src="./assets/Time Saving.png"
            alt="Virtual Healthcare Benefits"
          />
        </div>
      </section>

      {/* Virtual Triage Impact Section */}
      <section className="virtual-triage-impact">
        <h2>How Virtual Triage is Revolutionizing Virtual Care?</h2>
        <div className="impact-container">
          <div className="feature-card">
            <img src="./assets/human.svg" alt="AI-Powered Symptom Checker" />
            <h3>AI-Powered Symptom Analysis</h3>
            <p>
              Smart symptom checker that guides patients to the right care
              provider efficiently.
            </p>
          </div>
          <div className="feature-card">
            <img
              src="./assets/virtualcall.svg"
              alt="E-Payments & E-Prescriptions"
            />
            <h3>Instant Access to Doctors</h3>
            <p>
              Book an appointment with a licensed practitioner in just a few
              clicks.
            </p>
          </div>
          <div className="feature-card">
            <img
              src="./assets/e-payment.svg"
              alt="Secure & Transparent Payments"
            />
            <h3>Secure & Transparent Payments</h3>
            <p>Multiple payment options with complete price transparency.</p>
          </div>
          <div className="feature-card">
            <img src="./assets/icon-5.svg" alt="Global Healthcare Network" />
            <h3>Global Healthcare Network</h3>
            <p>Connect with doctors worldwide, no matter where you are.</p>
          </div>
        </div>
      </section>

      {/* Virtual Healthcare Accessibility */}
      <section className="healthcare-accessibility">
        <h2>Your Healthcare, Your Terms</h2>
        <p>
          Whether it's a late-night consultation, a second opinion, or urgent
          medical advice, Virtual Triage ensures that healthcare is always
          within your reach.
        </p>
        <a href="https://portal.virtualtraige.ca/login" className="cta-button">
          Get Started →
        </a>
      </section>

      <Footer />
    </div>
  );
}

export default Secondpage;
